// A record's audit timeline (Session AQW, W-6/W-7): "what happened to this record", read from the
// platform's own AuditLog via GET /api/audit-log?entity=&entityId=. A plain list, not a framework --
// this component only renders what the route already resolved (actor names, before/after) and
// fetches nothing itself, so the same component works whether a page loads it eagerly or on demand.
import { useState } from "react";
import type { AuditTimelineEntry } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { EmptyState } from "./EmptyState";
import { Loader } from "./Loader";

// Plain-English label for a raw action string (house rule: never show a raw enum/action key
// unadorned where a plain-English reading is easy to give). Actions not listed here fall back to a
// lightly humanised version of the key itself, so a new action added elsewhere never renders blank.
const ACTION_LABEL: Record<string, string> = {
  link_invoice: "Linked to an invoice",
  unlink_invoice: "Unlinked from the invoice",
  charge_computed: "Charge computed",
  charge_invoiced: "Invoice raised from the charge",
  assign: "Engineer assigned",
  report_callout_create: "Attendance report created",
  customerid_linked_on_report: "Customer linked from the report",
  approve: "Approved",
  tech_visit_create: "Tech visit raised",
  attendance_engineers_attributed: "Engineer attendance recorded",
  manual_create: "Manual record created",
  sent_to_client: "Sent to the customer",
  timesheet_entry_skipped: "Timesheet entry skipped",
};

function humanise(action: string): string {
  const known = ACTION_LABEL[action];
  if (known) return known;
  const words = action.replace(/[_-]+/g, " ").trim();
  return words.length > 0 ? words.charAt(0).toUpperCase() + words.slice(1) : action;
}

// An unparseable stamp is shown as written (a writer chose it); everything else reads the platform format.
function stamp(iso: string): string {
  return Number.isNaN(new Date(iso).getTime()) ? iso : formatDateTime(iso);
}

// A before/after Json value, rendered defensively: a plain object shows key: value pairs, anything
// else (a string, a number, an array, null) is shown as-is. Never throws on a shape a writer chose.
function DetailBlock({ label, value }: { label: string; value: unknown }) {
  if (value == null) return null;
  if (typeof value === "object" && !Array.isArray(value)) {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return null;
    return (
      <div className="mt-1">
        <span className="font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">{label}</span>
        <ul className="mt-0.5 space-y-0.5 pl-3 text-xs text-ele-body">
          {entries.map(([k, v]) => (
            <li key={k}>
              <span className="opacity-70">{k}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <p className="mt-1 text-xs text-ele-body">
      <span className="opacity-70">{label}: </span>
      {typeof value === "string" ? value : JSON.stringify(value)}
    </p>
  );
}

function TimelineRow({ entry }: { entry: AuditTimelineEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetail = entry.before != null || entry.after != null;
  return (
    <li className="rounded-ele border border-ele-hairline bg-ele-surface px-3 py-2 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-ele-strong">{humanise(entry.action)}</span>
        <span className="text-xs text-ele-body opacity-70">{stamp(entry.at)}</span>
      </div>
      <p className="mt-0.5 text-xs text-ele-body">{entry.actorName}</p>
      {hasDetail && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-1 text-xs text-ele-blue underline hover:text-ele-strong"
        >
          {expanded ? "Hide detail" : "Show detail"}
        </button>
      )}
      {expanded && (
        <div className="mt-1">
          <DetailBlock label="Before" value={entry.before} />
          <DetailBlock label="After" value={entry.after} />
        </div>
      )}
    </li>
  );
}

export function AuditTimeline({
  entries,
  loading,
  error,
  emptyMessage = "No activity recorded yet.",
}: {
  entries: AuditTimelineEntry[];
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
}) {
  if (loading) return <Loader rows={3} />;
  if (error) return <p role="alert" className="text-sm text-ele-danger">{error}</p>;
  if (entries.length === 0) return <EmptyState compact message={emptyMessage} />;
  return (
    <ul className="space-y-2">
      {entries.map((e, i) => (
        <TimelineRow key={`${e.action}-${e.at}-${i}`} entry={e} />
      ))}
    </ul>
  );
}
